// The MIT License (MIT)
//
// Copyright (c) 2016-2026 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import './desktop.css';
import gmfDesktopCanvasStyles from './gmf-desktop-canvas-shadow.css?inline';
import gmfAuthFormStyle from './gmf-auth-form.css?inline';
import gmfAuthPanelStyle from './gmf-auth-panel.css?inline';
import geoshopPanelStyle from './geoshop-panel.css?inline';
import toolButtonStyle from './tool-button.css?inline';
import {setLocale} from '../locale/localization.js';
import {styleShadowDom} from './commons/styleShadowDom.js';
import {moveModals} from './commons/moveModals.ts';
import './tenant.js';

document.addEventListener('DOMContentLoaded', () => {
  // Function to apply styles to shadow DOM elements
  const gmfDesktopCanvas = document.querySelector('gmf-desktop-canvas');
  if (gmfDesktopCanvas && gmfDesktopCanvas.shadowRoot) {
    styleShadowDom(gmfDesktopCanvas as HTMLElement, gmfDesktopCanvasStyles);
    const closeButton = gmfDesktopCanvas.shadowRoot.querySelector(
      '#gmf-app-tools-content .close',
    ) as HTMLElement;
    const updateCloseButtonVisibility = () => {
      if (!gmfDesktopCanvas.shadowRoot) {
        return;
      }
      const authSlot = gmfDesktopCanvas.shadowRoot.querySelector('slot[name="tool-panel-auth"]');
      const feedbackSlot = gmfDesktopCanvas.shadowRoot.querySelector('slot[name="tool-panel-feedback"]');
      if (authSlot || feedbackSlot) {
        closeButton.style.display = 'block'; // Show the close button if the auth slot has assigned nodes
      } else {
        closeButton.style.display = 'none'; // Hide the close button if the auth slot has no assigned nodes
      }
    };
    // Initial check
    updateCloseButtonVisibility();
    // Observe changes in the shadow DOM
    const observer = new MutationObserver(updateCloseButtonVisibility);
    observer.observe(gmfDesktopCanvas.shadowRoot, {childList: true, subtree: true});
  }
  const gmfAuthPanel = document.querySelector('gmf-auth-panel');
  if (gmfAuthPanel && gmfAuthPanel.shadowRoot) {
    styleShadowDom(gmfAuthPanel as HTMLElement, gmfAuthPanelStyle);
    // Traverse to gmf-auth-form within gmf-auth-panel
    const gmfAuthForm = gmfAuthPanel.shadowRoot.querySelector('gmf-auth-form');
    if (gmfAuthForm) {
      styleShadowDom(gmfAuthForm as HTMLElement, gmfAuthFormStyle);
    }
  }

  const geoshopPanel = document.querySelector('geoshop-panel');
  if (geoshopPanel && geoshopPanel.shadowRoot) {
    styleShadowDom(geoshopPanel as HTMLElement, geoshopPanelStyle);
    ['geoshop-cart', 'geoshop-order'].forEach((tag) => {
      let element = document.querySelector(tag);
      if (!element) {
        element = geoshopPanel.shadowRoot!.querySelector(tag);
      }
      styleShadowDom(element as HTMLElement, geoshopPanelStyle);
    });
  }

  const toolButtons = document.querySelectorAll(
    'gmf-auth-button, ' +
      'gmf-print-button, ' +
      'gmf-filter-button, ' +
      'gmf-draw-button, ' +
      'gmf-editing-button, ' +
      'gmf-profile-button, ' +
      'gmf-selection-button, ' +
      'gmf-feedback-button, ' +
      'gmf-import-button, ' +
      'geoshop-button',
  );

  toolButtons.forEach((element) => {
    if (element.shadowRoot) {
      styleShadowDom(element as HTMLElement, toolButtonStyle);
    }
  });
});

import angular from 'angular';
import gmfControllersAbstractDesktopController, {
  AbstractDesktopController,
} from 'gmf/controllers/AbstractDesktopController';
import geogr_gmfBase from './geogr_gmfmodule.js';
import gmfContextualdataModule from 'ngeo/contextualdata/module';
import ngeoStatemanagerWfsPermalink from 'ngeo/statemanager/WfsPermalink';
import '../webcomponents/index';
import contextualDataHtmlTemplate from '../ngeo_custom_templates/contextualdata.html';
import queryWindowTemplate from '../ngeo_custom_templates/queryWindowComponent.html';
import layertreeTemplate from '../ngeo_custom_templates/layertree.html';
import panelsModel from 'gmfapi/store/panels';
import {setLocale} from '../locale/localization.js';

/**
 * @private
 */
class Controller extends AbstractDesktopController {
  /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   */
  constructor($scope, $injector) {
    super($scope, $injector);
    this.initializeLocale();
    const langSwitcher = document.querySelector('language-switcher');
    if (langSwitcher) {
      langSwitcher.addEventListener('language-change', (event) => {
        const newLang = (event as CustomEvent).detail.lang;
        $scope.$apply(() => {
          this.lang = newLang;
          this.updateUrlLang(newLang);
          window.location.reload();
        });
      });
    }
    const search_params = new URLSearchParams(location.search);
    const geoshop = search_params.get('geoshop');
    if (geoshop !== null) {
      panelsModel.openToolPanel('geoshop');
    }

    // Move the modals to body when the modal-backdrop is added to the DOM to have the right stacking context
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof HTMLElement && node.classList.contains('modal-backdrop')) {
            moveModals();
          }
        }
      }
    });
    observer.observe(document.body, {childList: true, subtree: true});
  }

  async initializeLocale(): Promise<void> {
    await setLocale(this.lang);
  }

  /**
   * Updates the 'lang' parameter in the URL without reloading the page.
   */
  private updateUrlLang(newLang: string) {
    const url = new URL(window.location.href);
    url.searchParams.set('lang', newLang);
    window.history.replaceState({}, '', url.toString());
  }

  closeToolPanel() {
    panelsModel.closeToolPanel();
  }
}

Controller.$inject = ['$scope', '$injector'];

/**
 * @hidden
 */
const geogr_gmfModule = angular.module('Desktop', [
  geogr_gmfBase.name,
  gmfControllersAbstractDesktopController.name,
  gmfContextualdataModule.name,
  ngeoStatemanagerWfsPermalink.name,
]);
geogr_gmfModule.value('gmfContextualdatacontentTemplateUrl', 'gmf/contextualdata');
geogr_gmfModule.value('gmfDisplayquerywindowTemplateUrl', () => {
  return 'gmf/customquerywindow';
});
geogr_gmfModule.value('gmfLayertreeNodeTemplateUrl', () => {
  return 'gmf/customlayertree';
});
geogr_gmfModule.run(
  /**
   * @param {angular.ITemplateCacheService} $templateCache
   */
  [
    '$templateCache',
    ($templateCache) => {
      // @ts-ignore: webpack
      $templateCache.put('gmf/contextualdata', contextualDataHtmlTemplate);
      $templateCache.put('gmf/customquerywindow', queryWindowTemplate);
      $templateCache.put('gmf/customlayertree', layertreeTemplate);
    },
  ],
);
geogr_gmfModule.controller('DesktopController', Controller);
export default geogr_gmfModule;
